import React, { useMemo, useState, useLayoutEffect, useEffect } from "react";
import {
  AlertTriangle,
  HeartHandshake,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import TopBar      from "../components/TopBar";
import PageWrapper from "../components/layout/PageWrapper";
import { InlineIconButton } from "../components/Forms";
import SearchInput from "../components/SearchInput";
import { useDistrictGuard } from "../hooks/useDistrictGuard";

import { useVisitsAgenda, useVisitMetrics } from "@/features/visits/hooks";
import { getPastoralCoverage } from "@/features/visits/utils/pastoralCoverage";
import VisitRowCard from "@/features/visits/components/VisitRowCard";
import { normalizeText } from "../lib/search-rank";
import { SkeletonVisitCard } from "../components/shared/Skeletons";
import EmptyState from "../components/shared/EmptyState";
import { useCoordinatorChurchName } from "../hooks/useCoordinatorChurchName";
import { useRegisterKeviaContext } from "../contexts/KeviaContext";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const MONTHS_ES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

/* ─── Helpers ───────────────────────────────────────────────────────────── */

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function dateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const DAY_HEADERS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

/* ─── Sub-components ────────────────────────────────────────────────────── */

function HeroVisitas({ metrics, atrasadas, coverage, monthLabel, year, onPrev, onNext, loading, onOverdueTap }) {
  const stats = [
    { label: "Realizadas", value: loading ? "—" : metrics.completed    },
    { label: "Pendientes", value: loading ? "—" : metrics.pending       },
    {
      label:   "Atrasadas",
      value:   loading ? "—" : (atrasadas ?? "—"),
      onClick: !loading && atrasadas > 0 ? onOverdueTap : undefined,
    },
    {
      label:    "Cobertura",
      value:    loading ? "—" : (coverage ? `${coverage.pct}%` : "—"),
      subtitle: loading ? null : coverage?.label,
    },
  ];

  return (
    <div className="px-4 pt-3 pb-2 bg-page-bg">
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1A7FFF] via-[#0b3b82] to-[#0B1F3A] p-5 shadow-[0_8px_32px_rgba(27,79,156,0.25)] dark:shadow-none" style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/[0.06] to-transparent pointer-events-none" />
        <div className="absolute inset-0 rounded-[inherit] overflow-hidden pointer-events-none">
          <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-white/[0.06] blur-2xl" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-[16px] bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
              <HeartHandshake size={22} className="text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-widest text-white/60 font-medium">
                Agenda de visitación
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <button
                  onClick={onPrev}
                  className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-[0.92] transition-all duration-150"
                  aria-label="Mes anterior"
                >
                  <ChevronLeft size={15} className="text-white" />
                </button>
                <p className="text-[18px] font-semibold text-white">
                  {monthLabel} {year}
                </p>
                <button
                  onClick={onNext}
                  className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-[0.92] transition-all duration-150"
                  aria-label="Mes siguiente"
                >
                  <ChevronRight size={15} className="text-white" />
                </button>
              </div>
            </div>
          </div>

          <div className="h-px bg-white/10 my-4" />

          <div className="grid grid-cols-4 gap-2">
            {stats.map((s) => {
              const Tag = s.onClick ? "button" : "div";
              return (
                <Tag
                  key={s.label}
                  onClick={s.onClick}
                  className={`bg-white/10 rounded-[14px] px-2 py-2.5 text-center flex flex-col items-center justify-center ${s.onClick ? "active:bg-white/20 transition-colors duration-150" : ""}`}
                >
                  <p className="text-[18px] font-bold text-white leading-none">{s.value}</p>
                  <p className="text-[10px] text-white/60 mt-1 leading-none">{s.label}</p>
                  {s.subtitle && (
                    <p className="text-[9px] text-white/40 mt-1 leading-none italic">{s.subtitle}</p>
                  )}
                </Tag>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthCalendar({ year, month, selectedDay, onSelectDay, daysWithVisits, todayDay }) {
  const total       = getDaysInMonth(year, month);
  const startOffset = new Date(year, month - 1, 1).getDay(); // 0 = Sunday

  const cells = useMemo(() => {
    const arr = Array(startOffset).fill(null);
    for (let d = 1; d <= total; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [total, startOffset]);

  return (
    <div className="px-4 pt-1 pb-3">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-0.5">
        {DAY_HEADERS.map((h) => (
          <p key={h} className="text-center text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 py-1">
            {h}
          </p>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} className="h-11" />;
          const active  = day === selectedDay;
          const isToday = day === todayDay;
          const hasDot  = daysWithVisits.has(day);

          // Cell background + ring
          const cellCls = active
            ? [
                "bg-[#2e60b0] shadow-[0_2px_8px_rgba(46,96,176,0.25)]",
                isToday ? "ring-2 ring-white/40 ring-offset-1 ring-offset-[#2e60b0]" : "",
              ].join(" ")
            : isToday
              ? "bg-[#2e60b0]/10 dark:bg-[#2e60b0]/20 ring-1 ring-[#2e60b0]/30 dark:ring-[#4a80d4]/40 hover:bg-[#2e60b0]/15 dark:hover:bg-[#2e60b0]/25"
              : "hover:bg-blue-100/60 dark:hover:bg-blue-900/15";

          // Number color
          const numCls = active
            ? "text-white"
            : isToday
              ? "text-[#2e60b0] dark:text-blue-300"
              : "text-slate-800 dark:text-slate-100";

          // Visit dot color
          const dotCls = hasDot
            ? active
              ? "bg-white/70"
              : isToday
                ? "bg-[#2e60b0]/60 dark:bg-blue-300/60"
                : "bg-[#2e60b0]/50"
            : "bg-transparent";

          return (
            <button
              key={day}
              onClick={() => onSelectDay(day)}
              className={`
                h-11 w-full flex flex-col items-center justify-center gap-0.5
                rounded-[10px] transition-all duration-200 ease-out
                active:scale-[0.96] active:opacity-80
                ${cellCls}
              `}
            >
              <span className={`text-[14px] font-semibold leading-none ${numCls}`}>
                {day}
              </span>
              <span className={`h-1 w-1 rounded-full ${dotCls}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => <SkeletonVisitCard key={i} />)}
    </div>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function Visits() {
  const { district, coordinatorChurchId } = useDistrictGuard();
  const coordinatorChurchName = useCoordinatorChurchName();

  const today  = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();
  const todayStr = `${todayY}-${String(todayM).padStart(2,"0")}-${String(todayD).padStart(2,"0")}`;

  const [year,        setYear]        = useState(todayY);
  const [month,       setMonth]       = useState(todayM);
  const [selectedDay, setSelectedDay] = useState(todayD);
  const [search,      setSearch]      = useState("");
  const [showOverdue, setShowOverdue] = useState(false);

  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem("keves:visits:scrollY");
      if (saved) { window.scrollTo(0, parseInt(saved, 10)); sessionStorage.removeItem("keves:visits:scrollY"); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    return () => { try { sessionStorage.setItem("keves:visits:scrollY", String(Math.round(window.scrollY))); } catch { /* ignore */ } };
  }, []);

  const { visitsByDate, daysWithVisits, loading, error, refetch, allVisits } = useVisitsAgenda(year, month);
  const { metrics }                                                   = useVisitMetrics(allVisits, loading);

  const monthLabel = MONTHS_ES[month - 1];

  const atrasadas = useMemo(() => {
    if (loading) return null;
    return Object.values(visitsByDate).flat()
      .filter((v) => v.status === "pendiente" && v.date < todayStr).length;
  }, [visitsByDate, loading, todayStr]);

  const overdueVisits = useMemo(() => {
    if (loading) return [];
    return Object.values(visitsByDate).flat()
      .filter((v) => v.status === "pendiente" && v.date < todayStr)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [visitsByDate, loading, todayStr]);

  const coverage = useMemo(() => {
    if (loading || metrics.completed == null) return null;
    return getPastoralCoverage(metrics);
  }, [metrics, loading]);

  const handlePrev = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); setSelectedDay(1); }
    else             { setMonth((m) => m - 1); setSelectedDay(1); }
  };

  const handleNext = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); setSelectedDay(1); }
    else              { setMonth((m) => m + 1); setSelectedDay(1); }
  };

  const dayVisits = useMemo(
    () => visitsByDate[dateKey(year, month, selectedDay)] || [],
    [visitsByDate, year, month, selectedDay]
  );

  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = normalizeText(search);
    return Object.values(visitsByDate).flat().filter((v) =>
      normalizeText(v.contact_name || "").includes(q) ||
      normalizeText(v.notes || "").includes(q)
    );
  }, [visitsByDate, search]);

  // Kevia dynamic context — registered while this page is mounted
  const keviaCtx = useMemo(() => {
    if (loading) return null;
    const lines = [
      `Módulo: Visitación pastoral`,
      `  Mes en vista: ${monthLabel} ${year}`,
      `  Realizadas: ${metrics.completed ?? "—"}`,
      `  Pendientes: ${metrics.pending ?? "—"}`,
      atrasadas != null && atrasadas > 0 && `  Atrasadas: ${atrasadas}`,
      coverage && `  Cobertura pastoral: ${coverage.pct}%${coverage.label ? ` (${coverage.label})` : ""}`,
    ].filter(Boolean);
    return {
      type:         "module",
      id:           `visits-${year}-${month}`,
      name:         `Visitas ${monthLabel} ${year}`,
      tone:         atrasadas > 0 ? "followup_needed" : "neutral",
      contextBlock: `[Contexto activo en Kéves]\n${lines.join("\n")}\n\n[Pregunta del pastor]\n`,
    };
  }, [loading, year, month, monthLabel, metrics, atrasadas, coverage]);
  useRegisterKeviaContext(keviaCtx);

  return (
    <PageWrapper onRefresh={refetch}>
      <TopBar
        title="Visitación"
        subtitle={coordinatorChurchId ? (coordinatorChurchName || undefined) : district?.name}
        safeFallback="/district"
        right={
          <InlineIconButton
            icon={Plus}
            to="/visits/new"
            accent="navy"
            aria-label="Nueva visita"
          />
        }
      />

      {/* Search — above hero */}
      <div className="px-4 pt-2 pb-1">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar visita por nombre o nota..."
        />
      </div>

      <HeroVisitas
        metrics={metrics}
        atrasadas={atrasadas}
        coverage={coverage}
        monthLabel={monthLabel}
        year={year}
        onPrev={handlePrev}
        onNext={handleNext}
        loading={loading}
        onOverdueTap={() => setShowOverdue(true)}
      />

      {searchResults ? (
        /* Search results — flat list across full month */
        <div className="px-4 pt-2 space-y-2">
          {searchResults.length === 0 ? (
            <EmptyState compact icon={<HeartHandshake size={20} />} title={`Sin resultados para "${search}"`} />
          ) : (
            searchResults.map((v) => <VisitRowCard key={v.id} visit={v} />)
          )}
        </div>
      ) : showOverdue ? (
        /* Overdue visits — tapped from "Atrasadas" stat */
        <div className="px-4 pt-2">
          <div className="flex items-center justify-between pb-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Visitas pendientes
            </p>
            <button
              onClick={() => setShowOverdue(false)}
              className="text-[11px] text-slate-400 dark:text-slate-500 active:opacity-70 transition-opacity duration-150"
            >
              Ver calendario
            </button>
          </div>
          {overdueVisits.length === 0 ? (
            <EmptyState compact icon={<HeartHandshake size={20} />} title="Sin visitas pendientes atrasadas" />
          ) : (
            <div className="space-y-2">
              {overdueVisits.map((v) => <VisitRowCard key={v.id} visit={v} />)}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Monthly calendar grid */}
          <MonthCalendar
            year={year}
            month={month}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            daysWithVisits={daysWithVisits}
            todayDay={year === todayY && month === todayM ? todayD : null}
          />

          {/* Day visits */}
          {loading && dayVisits.length === 0 ? (
            <div className="px-4 pt-2"><SkeletonCards /></div>
          ) : !loading && error ? (
            <div className="px-4 pt-2">
              <EmptyState
                icon={<AlertTriangle size={28} />}
                title="No se pudo cargar"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                actionOnClick={refetch}
              />
            </div>
          ) : dayVisits.length === 0 ? (
            <div className="animate-fade py-10 flex flex-col items-center gap-2.5 px-4">
              <div className="h-12 w-12 rounded-[14px] bg-blue-50 dark:bg-blue-950/20 flex items-center justify-center">
                <HeartHandshake size={20} className="text-[#2e60b0]/60 dark:text-[#2e60b0]/40" />
              </div>
              <p className="text-[13px] text-slate-400 dark:text-slate-500 leading-snug">
                {year === todayY && month === todayM && selectedDay === todayD
                  ? "Sin visitas hoy"
                  : dateKey(year, month, selectedDay) < todayStr
                  ? "Sin visitas registradas"
                  : "Sin visitas programadas"}
              </p>
            </div>
          ) : (
            <div className="px-4 pt-2 space-y-2">
              {dayVisits.map((v) => (
                <VisitRowCard key={v.id} visit={v} />
              ))}
            </div>
          )}
        </>
      )}
      <div className="pb-[calc(24px+env(safe-area-inset-bottom))]" />
    </PageWrapper>
  );
}
